import { memo } from 'react';
import { IconButton, Typography, Link as MuiLink } from '@mui/material';
import { withStyles } from '@mui/styles';
import BuildIcon from '@mui/icons-material/Build';
import LaunchIcon from '@mui/icons-material/Launch';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import Tooltip from '../../../../components/GlobalComponents/ToolTipComponent';
import CellLink from './CellLink';
import formatDate, { formatUtcDate, isMidnightUtc } from '../../../../utils/formatDate';
import UpdateStatusButton from '../UpdateStatusButton';
import { getSalesforceBookingLink, getSalesforceContactLink } from '../../../../utils/helpers';
import { COLORS } from '../../../../utils/Application_Constants';
import { ProcessedTimesheetContextProvider } from '../../../Contexts/ProcessedTimesheetContext';

const styles = () => ({
    needReview: {
        display: 'flex',
        flexDirection: 'column',

        '& span': {
            maxWidth: '100%',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            textWrap: 'nowrap',
        },
    },
    tooltip: { borderRadius: '8px' },
});

function BodyCell({ classes, pts, column }) {
    if (column.key === 'status') {
        return (
            <ProcessedTimesheetContextProvider>
                <UpdateStatusButton
                    id={pts.id}
                    variant="lt-admin-pill-outlined"
                    status={pts.status}
                    needsReviewStatus={pts.needsReviewStatus}
                    clinicianName={pts.clinicianName}
                    lastModified={pts.lastModified}
                    isCorrection={pts.isCorrection}
                    previousCreditMemoMode={pts?.creditMemoMode}
                    monthPostedDate={pts.monthPosted}
                />
            </ProcessedTimesheetContextProvider>
        );
    }

    let content;
    let showOverflow = false;
    /**
     * Renders the body cell based on the column key
     */
    switch (column.key) {
        case 'isCorrection':
            if (pts.isCorrection) {
                content = (
                    <Tooltip title="This timesheet has corrections">
                        <BuildIcon
                            sx={{
                                color: COLORS.LT_POMEGRANATE,
                                verticalAlign: 'middle',
                                width: 17,
                                height: 17,
                            }}
                        />
                    </Tooltip>
                );
            } else {
                content = '';
            }
            break;
        case 'processedTimesheetId':
            content = (
                <Tooltip
                    classes={{ tooltip: classes.tooltip }}
                    title={
                        <Typography variant="body2">
                            View timesheet.
                            <br />
                            ID is for LT Admin search only. Does not match Workday PTS IDs.
                        </Typography>
                    }
                    placement="right"
                    arrow
                >
                    <MuiLink
                        component={Link}
                        to={`/processed-timesheets/${pts.id}`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {pts.id}
                    </MuiLink>
                </Tooltip>
            );
            break;
        case 'processedTimesheet':
            content = (
                <IconButton
                    href={`#/processed-timesheets/${pts.id}`}
                    target="_blank"
                    rel="noreferrer"
                    color="primary"
                    size="small"
                    aria-label="View timesheet"
                    title="View timesheet"
                >
                    <LaunchIcon />
                </IconButton>
            );
            break;
        case 'recruiter':
            if (pts.recruiterEmail && pts.recruiterName) {
                content = (
                    <CellLink href={`mailto:${pts.recruiterEmail}`} label={pts.recruiterName} />
                );
            } else {
                content = pts.recruiterName;
            }
            break;
        case 'salesperson':
            if (pts.salespersonEmail && pts.salespersonName) {
                content = (
                    <CellLink href={`mailto:${pts.salespersonEmail}`} label={pts.salespersonName} />
                );
            } else {
                content = pts.salespersonName;
            }
            break;
        case 'clinician':
            if (pts.clinicianSalesforceId && pts.clinicianName) {
                content = (
                    <CellLink
                        href={getSalesforceContactLink(pts.clinicianSalesforceId)}
                        label={pts.clinicianName}
                    />
                );
            } else {
                content = pts.clinicianName;
            }
            break;
        case 'booking':
            if (pts.bookingSalesforceId && pts.bookingId) {
                content = (
                    <CellLink
                        href={getSalesforceBookingLink(pts.bookingSalesforceId)}
                        label={`BKG-${pts.bookingId}`}
                    />
                );
            } else {
                content = `BKG-${pts.bookingId || 'Unknown'}`;
            }
            break;
        case 'needsReviewReason': {
            let needsReviewReasons = [];
            // TODO: Remove this once the API is updated to return an array of strings
            if (typeof pts.needsReviewReason === 'string') {
                needsReviewReasons = pts.needsReviewReason.split(';');
            } else if (Array.isArray(pts.needsReviewReason)) {
                needsReviewReasons = pts.needsReviewReason;
            }
            content = (
                <span className={classes.needReview}>
                    {needsReviewReasons?.map((reason, index) => (
                        <span
                            // eslint-disable-next-line react/no-array-index-key
                            key={`ts:${pts.id}|reason:${index}`}
                            title={reason}
                        >
                            {reason}
                            {needsReviewReasons?.length > 1 &&
                                index !== needsReviewReasons.length - 1 &&
                                ';'}
                        </span>
                    ))}
                </span>
            );
            showOverflow = true;
            break;
        }
        case 'teamCoordinatorComments':
            content = <span>{pts.teamCoordinatorComments}</span>;
            showOverflow = true;
            break;
        case 'clinicianComments':
            content = pts.providerComments;
            showOverflow = true;
            break;
        case 'weekEndingDate':
            content = formatUtcDate(pts.weekEnding);
            break;
        case 'ab5':
            content = pts.aB5 ? 'W2' : '';
            break;
        case 'originalPay':
            content = pts.originalPayrollDate ? formatUtcDate(pts.originalPayrollDate) : '';
            break;
        case 'approvalDate':
            content = pts.approvalDate ? formatDate(pts.approvalDate) : '';
            break;
        case 'monthPosted':
            content = pts.monthPosted
                ? isMidnightUtc(pts.monthPosted)
                    ? formatUtcDate(pts.monthPosted)
                    : formatDate(pts.monthPosted)
                : '';
            break;
        default:
            content = pts?.[column.key] || '';
            break;
    }
    return (
        <Typography
            variant="body2"
            className={showOverflow ? 'show-overflow' : ''}
            data-testid="body-cell-content"
            title={typeof content === 'string' ? content : ''}
        >
            {content}
        </Typography>
    );
}

const StyledBodyCell = withStyles(styles)(BodyCell);
export default memo(StyledBodyCell);
